// A shade grid (15 Oct 2026): rows of equal squares. As a picture it is an
// empty grid, or (1 Nov 2026) one with its first `grid.shaded` squares
// filled, row by row, each with a dot as on the answer control. Given
// `onToggle` it is the answer control for "shade the
// parts" questions (PracticeTask.tsx): every square is a real toggle
// button, and a shaded square is filled AND carries a dot, so it is told
// apart without colour.
//
// The same control shades the parts of a fraction model (a bar, a circle's
// sectors are not tappable shapes in HTML, so a model is shaded as a row or
// grid of its parts; see ShadeAnswer in PracticeTask.tsx).

import {
  describeShadeGrid,
  type ShadeGridSpec,
} from './practice-shade-grid-data';

export function ShadeGridView({
  grid,
  shaded = [],
  disabled = false,
  onToggle,
}: {
  grid: ShadeGridSpec;
  // The places of the shaded squares, counted from 0 along each row.
  shaded?: number[];
  disabled?: boolean;
  // Given only where the grid is the answer control.
  onToggle?: ((index: number) => void) | undefined;
}) {
  const squares = Array.from(
    { length: grid.rows * grid.columns },
    (_, index) => index,
  );
  // A square is at most 44px, and the grid never wider than its box.
  const style = {
    gridTemplateColumns: `repeat(${String(grid.columns)}, minmax(0, 1fr))`,
    maxWidth: `${String(grid.columns * 44)}px`,
  };
  const cell =
    'relative aspect-square border border-[#2d4a3e] -mb-px -mr-px flex items-center justify-center';
  if (!onToggle)
    return (
      <figure className="m-0">
        <div
          role="img"
          aria-label={describeShadeGrid(grid)}
          className="grid w-full pb-px pr-px"
          style={style}
        >
          {squares.map((index) =>
            index < grid.shaded ? (
              <span
                key={index}
                data-part="shaded"
                className={`${cell} bg-[#9fc5b3]`}
              >
                <span className="size-1.5 rounded-full bg-[#2d4a3e]" />
              </span>
            ) : (
              <span key={index} className={`${cell} bg-white`} />
            ),
          )}
        </div>
      </figure>
    );
  return (
    <div
      role="group"
      aria-label={describeShadeGrid(grid)}
      className="grid w-full pb-px pr-px"
      style={style}
    >
      {squares.map((index) => {
        const on = shaded.includes(index);
        return (
          <button
            key={index}
            type="button"
            aria-pressed={on}
            aria-label={`Row ${String(Math.floor(index / grid.columns) + 1)}, square ${String((index % grid.columns) + 1)}`}
            disabled={disabled}
            data-part={on ? 'shaded' : 'empty'}
            className={`${cell} cursor-pointer focus-visible:z-10 focus-visible:outline-3 focus-visible:outline-offset-0 focus-visible:outline-forest-600 ${
              on ? 'bg-[#9fc5b3]' : 'bg-white'
            }`}
            onClick={() => {
              onToggle(index);
            }}
          >
            {on && (
              <span
                aria-hidden="true"
                className="size-1.5 rounded-full bg-[#2d4a3e]"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
